/**
 * Bike Hero Section Component for Product Category Pages
 * 
 * This component creates compelling hero sections for bike category pages
 * (MTB, Road, Gravel) with full-screen visuals and call-to-action elements.
 * 
 * Features:
 * - Full-viewport height hero layout
 * - Dynamic background image from Sanity CMS
 * - Fallback to default images for reliability
 * - Responsive typography with Neuropol brand font
 * - Gradient overlay for text readability
 * - Prominent call-to-action button
 * - Priority loading for hero images (LCP optimization)
 * 
 * Visual Design:
 * - Full-screen background images with object-cover
 * - Dark gradient overlay (black 60% to transparent)
 * - Left-aligned content positioning
 * - Responsive padding and spacing
 * - High-contrast white text on dark overlay
 * 
 * Accessibility:
 * - Semantic heading structure
 * - Descriptive alt text for background images
 * - Sufficient color contrast for text readability
 * - Focus-visible button states
 * 
 * @param {object} backgroundImage - Sanity image object for hero background
 * @param {string} title - Main hero heading text
 * @param {string} buttonLabel - Text for the call-to-action button
 * @param {string} defaultBackgroundImage - Fallback image URL
 * @param {function} onButtonClick - Click handler for CTA button
 * 
 * @description Full-screen hero section for bike category pages with CMS integration
 */

import Image from 'next/image';
import { urlForImage } from '../../src/app/studio/lib/image';
import { sanityLoader } from '../../utils/sanityLoader';

const BikeHeroSection = ({ 
  backgroundImage, 
  title, 
  buttonLabel, 
  defaultBackgroundImage,
  onButtonClick 
}) => {
  // Get the image URL safely, falling back to the default if needed
  const imageUrl = backgroundImage ? urlForImage(backgroundImage) : null;
  const finalImageUrl = imageUrl || defaultBackgroundImage;

  return (
    <section className="relative h-screen w-full overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        {finalImageUrl && (
        <Image
            src={finalImageUrl}
          alt={title || "Bike"}
          fill
          loader={sanityLoader}
          className="object-cover"
          priority
          sizes="100vw"
        />
        )}
      </div>
      
      {/* Overlay Gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-transparent"></div>

      {/* Content */}
      <div className="relative h-full flex flex-col items-start justify-center px-8 md:px-16 lg:px-24">
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-8 font-neuropol">
          {title}
        </h1>
        
        {/* Hero Button */}
        <button 
          onClick={onButtonClick}
          className="px-6 py-3 bg-white text-black font-bold rounded-md hover:bg-opacity-90 transition duration-300 ease-in-out"
        >
          {buttonLabel}
        </button>
      </div>
    </section>
  );
};

export default BikeHeroSection; 